import React, { useMemo, useState } from "react";
import Table from "../../../components/Table";
import TitleButtons from "../../../components/TitleButtons";
import { generatePdfReport } from "../../../utils/pdf/generatePdfReport";
import numeroParaExtenso, {
  numeroMonetarioParaExtenso,
} from "../../../utils/numeroPorExtenso";

import { useEstoqueDisponivel } from "./estoqueData";
import { useNavigate } from "react-router-dom";

const SuprimentosEstoque: React.FC = () => {
  const navigate = useNavigate();
  const { itens, loading } = useEstoqueDisponivel();

  const headers = [
    "Posição",
    "Código",
    "NCM",
    "Nome do Produto",
    "Custo Unitário",
    "Moeda",
    "Quantidade",
    "Classificação",
  ];

  const columnWidths = [
    "10px",
    "70px",
    "70px",
    "350px",
    "100px",
    "60px",
    "10px",
    "50px",
  ];

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = selectedColumns
    .map((checked, i) => (checked ? i : -1))
    .filter((i) => i !== -1);

  /** ------------------ formatação por moeda ------------------ */
  const formatCurrency = (
    valor: string | number | null | undefined,
    moeda?: string | null
  ): string => {
    if (!moeda) return "N/A";
    const n = typeof valor === "string" ? Number(valor) : valor ?? 0;
    if (!isFinite(n)) return "N/A";

    const symbol =
      moeda === "USD"
        ? "$"
        : moeda === "EUR"
        ? "€"
        : moeda === "BRL"
        ? "R$"
        : "";

    if (!symbol) return "N/A";

    return `${symbol} ${n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const moedaFromItem = (p: any) =>
    p?.moeda && String(p.moeda).trim() !== "" ? String(p.moeda) : null;

  // linhas da tabela (tela)
  const rows = useMemo(
    () =>
      itens.map((p) => {
        const moeda = moedaFromItem(p);
        return [
          p.posicao ?? "",
          p.codigo ?? "",
          p.ncm ?? "",
          p.nomeProduto ?? "",
          formatCurrency(p.custoUnitario, moeda),
          moeda ?? "N/A",
          String(p.quantidade ?? 0),
          p.classificacao ?? "",
        ];
      }),
    [itens]
  );

  // base para relatório: só itens com quantidade > 0
  const itensRelatorio = useMemo(
    () => itens.filter((p) => (p.quantidade ?? 0) > 0),
    [itens]
  );

  const rowsReport = useMemo(
    () =>
      itensRelatorio.map((p) => {
        const moeda = moedaFromItem(p);
        return [
          p.posicao ?? "",
          p.codigo ?? "",
          p.ncm ?? "",
          p.nomeProduto ?? "",
          formatCurrency(p.custoUnitario, moeda),
          moeda ?? "N/A",
          String(p.quantidade ?? 0),
          p.classificacao ?? "",
        ];
      }),
    [itensRelatorio]
  );

  // Quantidade total de itens (somando tudo, independente de moeda)
  const totalProdutosRel = useMemo(
    () => itensRelatorio.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itensRelatorio]
  );

  // === NOVO: total por moeda (BRL, USD, EUR, etc.) ===
  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    itensRelatorio.forEach((p) => {
      const moeda = moedaFromItem(p);
      if (!moeda) return;

      const custo = Number(p.custoUnitario ?? 0);
      const qtd = p.quantidade ?? 0;
      const subtotal = isFinite(custo * qtd) ? custo * qtd : 0;

      if (!acc[moeda]) acc[moeda] = 0;
      acc[moeda] += subtotal;
    });

    return acc;
  }, [itensRelatorio]);

  // === NOVO: lines de rodapé do relatório usando totais por moeda ===
  const footerLines = useMemo(() => {
    const lines: string[] = [];

    // linha com total de produtos (independente de moeda)
    lines.push(`Total de Produtos no Estoque: ${totalProdutosRel}`);

    const entries = Object.entries(totaisPorMoeda);
    if (!entries.length) return lines;

    // ordenar por moeda só pra ficar organizado
    entries.sort(([a], [b]) => a.localeCompare(b));

    entries.forEach(([moeda, total]) => {
      const symbol =
        moeda === "USD"
          ? "$"
          : moeda === "EUR"
          ? "€"
          : moeda === "BRL"
          ? "R$"
          : "";

      const formatted = `${symbol ? symbol + " " : ""}${total.toLocaleString(
        "pt-BR",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}`;

      // >>> NOVO: texto por extenso para QUALQUER moeda
      const extensoMoeda = numeroMonetarioParaExtenso(total, moeda);

      const line = `Valor Total em Estoque (${moeda}): ${formatted} (${extensoMoeda})`;

      lines.push(line);
    });

    return lines;
  }, [totalProdutosRel, totaisPorMoeda]);

  const codigoIdx = headers.indexOf("Código");

  return (
    <div>
      <TitleButtons
        title={"Suprimentos - Estoque"}
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Estoque - Suprimentos",
                fileNamePrefix: "Suprimentos-Estoque",
                subtitle: "Informações dos Produtos no Estoque",
                headers,
                rows: rowsReport,
                custoColIndex: 4,
                enabledColumns: enabledColumnIndexes,
                footerLines, // <<< AGORA VAI O FOOTER DINÂMICO
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
          {
            label: "Realizar Movimentação",
            onClick: () =>
              navigate("/suprimentos/estoque/realizar-movimentacao"),
            variant: "primary",
          },
        ]}
      />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando estoque…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "");
            if (codigo) {
              navigate(
                `/suprimentos/produtos/${encodeURIComponent(codigo)}/historico`
              );
            }
          }}
          responsiveWidths={{
            1300: {
              1: "10px",
              2: "70px",
              3: "0px",
              4: "350px",
              5: "100px",
              6: "60px",
              7: "10px",
              8: "50px",
            },
          }}
        />
      )}
    </div>
  );
};

export default SuprimentosEstoque;
